import React from "react";
import {Box, Drawer, IconButton, Typography} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";

import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";

/**
 * What a dimension is, and what a measure is — as a drawer rather than a tooltip.
 *
 * A TOOLTIP WAS THE WRONG CONTAINER FOR THIS. It has to be held open by the pointer, so it cannot
 * be read slowly, cannot be scrolled, and disappears the moment somebody looks back at the shelves
 * to apply what they just read. Five paragraphs in a hover card is a paragraph nobody finishes.
 *
 * WRITTEN FROM THE COMPILER, NOT FROM THE IDEA. Every clause named here was checked against
 * report_query.py rather than recalled: a dimension is appended to the SELECT list AND to GROUP BY;
 * a measure is wrapped in its aggregation in SELECT only; sorting builds ORDER BY from either; and
 * a filter reaches HAVING only when it is marked afterGrouping. Documentation describing a tool as
 * intended rather than as built is worse than none, because it is believed.
 *
 * NAMING THE SQL IS RIGHT FOR THIS AUDIENCE even though nobody here writes any. "Appears in GROUP
 * BY" tells somebody who has met a database exactly what a dimension is, in four words.
 */

type Clause = {clause: string; what: string};
type Help = {
    title: string;
    lead: string;
    body: string[];
    clauses: Clause[];
    footnote?: string;
};

export const ROLE_HELP: Record<string, Help> = {
    dimension: {
        title: "Dimensions",
        lead: "A dimension does two things at once: it becomes a column of the result, and it decides what one row means.",
        body: [
            "That is the thing most worth knowing, because it is not how SQL is usually written. There is no separate step for choosing columns here: every field you put on a shelf appears in the result. Group by Owning Department and you both SEE the department and get one row per department.",
            "Add Procedure Number and you see that too, and get one row per procedure. Every dimension you add makes the answer finer and the rows more numerous; every one you take off rolls them up.",
            "Every dimension you add makes the answer finer and the rows more numerous. Every one you take off rolls them up. Only combinations that actually occur come back — it is not a cross-product.",
            "Text, dates and Yes/No fields are all dimensions. A date is one even though it is not text: “average effective date” is not a thing anyone wants. What you want is one row per month — group it by part, and the server truncates the date to that grain.",
        ],
        clauses: [
            {clause: "SELECT", what: "the value itself \u2014 this is why it shows up as a column"},
            {clause: "GROUP BY", what: "the same expression again — this is what creates one row per distinct value"},
            {clause: "ORDER BY", what: "when you sort on it"},
            {clause: "WHERE", what: "when you filter on it — applied before the grouping, so it decides which records are counted at all"},
        ],
        footnote: "On Summarize a dimension can only be counted. The server refuses to average words, so the screen does not offer what would be refused.",
    },
    // THE THIRD THING, AND THE ONE PEOPLE ASK FOR FIRST. Reached from the same help icon, because
    // "which fields do I see?" and "why did my two records become one?" are the same question.
    listing: {
        title: "One row per record",
        lead: "Set One row per to “record” and a dimension does only the first of its two jobs: it becomes a column, and nothing is combined.",
        body: [
            "This is the report that just lists things. Procedure number, title, department, effective date — one line per SOP, in the order you sort them. No totals, no counting, no rolling up.",
            "It is worth knowing what the other mode does instead, because it looks the same until it isn’t. One row per GROUP is SELECT DISTINCT: two SOPs that agree on every column you picked come back as one line, and you cannot tell from looking. If you want a list of records, say so here rather than hoping the columns you chose happen to be unique.",
            "Summarize is unavailable in this mode, and the Σ buttons grey out — there are no groups, so there is nothing to total. Filtering on a total and top-N go with them. Everything else works: filters apply to each record, sorting is yours, and a date can still be shown truncated to its month.",
            "The result is always ordered, even when you do not sort it. The server adds a tiebreaker of its own so that running the same report twice gives you the rows in the same order — which matters, because every run is kept as a record of what the data said at that moment.",
            "A listing is capped at 500 rows rather than the 10,000 an aggregate may return. The panel tells you when you have hit it: a list quietly cut short is a wrong answer, not a slow one.",
            "Each row opens the record it came from. The last column of a listing carries a link into QMS — a real one, so middle-click and open-in-new-tab work. A summary row has no such link, and could not: a summary row is a group, and there is no single record to open.",
        ],
        clauses: [
            {clause: "SELECT", what: "your columns, in the order you put them on the shelf"},
            {clause: "GROUP BY", what: "absent — that is the whole difference"},
            {clause: "WHERE", what: "your filters, applied to each record"},
            {clause: "ORDER BY", what: "your sort, then a tiebreaker the server adds"},
        ],
        footnote: "Press Query under the result to see it: switch between the two modes and watch GROUP BY appear and disappear.",
    },
    measure: {
        title: "Measures",
        lead: "A measure becomes a column too — holding that group's total rather than a value from any one record.",
        body: [
            "Summed, averaged, counted, or its smallest or largest taken. Dimensions divide the rows; measures fill them in.",
            "So the columns you get are simply everything on Group by, then everything on Summarize, in that order. Nothing appears that you did not place, and nothing you placed is left out.",
            "A field is a measure because it is a Number on the form it came from. Nothing tags it by hand, so adding a numeric field to a form adds a measure here.",
            "A calculation — arithmetic over two numbers — behaves as a measure too, worked out per record and then summarized like any other.",
        ],
        clauses: [
            {clause: "SELECT", what: "wrapped in the aggregation you chose — SUM(…), AVG(…)"},
            {clause: "GROUP BY", what: "never. That is the whole difference from a dimension"},
            {clause: "ORDER BY", what: "over the aggregate, so “largest first” means largest total rather than largest single record"},
            {clause: "WHERE or HAVING", what: "your choice: before the grouping it drops records; after it, it drops whole groups whose TOTAL fails the test"},
        ],
        footnote: "A running total, share or rank is worked out across the finished rows, after everything above has happened.",
    },
};

type Props = {
    role: string | null;
    onClose: () => void;
};

export default function FieldRoleHelp({role, onClose}: Props) {
    const help = role ? ROLE_HELP[role] : undefined;

    return (
        <Drawer anchor="right" open={!!help} onClose={onClose}
                // slotProps, NOT PaperProps: MUI v9 removed the latter. tsc catches this one,
                // which is not true of every v9 change in this codebase.
                slotProps={{paper: {sx: {width: {xs: "100%", sm: 460}, p: 0}}}}>
            {help ? (
                <Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between",
                              px: 3, py: 2, borderBottom: "1px solid #E4E7EC"}}>
                        <Typography sx={{fontSize: 18, fontWeight: 600, color: Grey17}}>
                            {help.title}
                        </Typography>
                        <IconButton aria-label="Close" onClick={onClose} size="small">
                            <CloseOutlined sx={{fontSize: 18, color: Grey6}}/>
                        </IconButton>
                    </Box>

                    <Box sx={{px: 3, py: 2.5, overflowY: "auto"}}>
                        <Typography sx={{fontSize: 14, color: Grey17, fontWeight: 500, mb: 2, lineHeight: 1.6}}>
                            {help.lead}
                        </Typography>

                        {help.body.map((line, at) => (
                            <Typography key={at} sx={{fontSize: 13.5, color: Grey5, mb: 1.75, lineHeight: 1.65}}>
                                {line}
                            </Typography>
                        ))}

                        {/* THE PART SOMEBODY CAME FOR. Where the field actually turns up in the
                            statement the server builds -- which is the difference between knowing
                            the word and knowing what it does. */}
                        <Typography sx={{fontSize: 11, color: Grey6, textTransform: "uppercase",
                                         letterSpacing: "0.4px", mt: 3, mb: 1.25}}>
                            Where it appears in the query
                        </Typography>

                        <Box component="dl" sx={{m: 0}}>
                            {help.clauses.map(one => (
                                <Box key={one.clause} sx={{display: "flex", gap: 1.5, mb: 1.25,
                                                           alignItems: "baseline"}}>
                                    <Typography component="dt"
                                                sx={{fontSize: 12, fontWeight: 700, color: Blue3,
                                                     fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                                     minWidth: 128, flexShrink: 0}}>
                                        {one.clause}
                                    </Typography>
                                    <Typography component="dd"
                                                sx={{fontSize: 13, color: Grey5, m: 0, lineHeight: 1.55}}>
                                        {one.what}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {help.footnote ? (
                            <Box sx={{mt: 3, p: 1.75, borderRadius: "6px",
                                      backgroundColor: "rgba(20,152,216,0.08)"}}>
                                <Typography sx={{fontSize: 12.5, color: Grey5, lineHeight: 1.6}}>
                                    {help.footnote}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            ) : null}
        </Drawer>
    );
}
